import { useEffect, useState } from "react";

// Same hash as backend jarvis_hero.anchor_key — anchors saved as site content "hero.anchor_<hash>"
export const anchorKey = (src) => {
  let h = 5381;
  for (let i = 0; i < src.length; i += 1) h = (Math.imul(h, 33) ^ src.charCodeAt(i)) >>> 0;
  return `hero.anchor_${h.toString(36)}`;
};

// AI-detected door box + body outline for the built-in wash cars (image % coords)
const A = (car, door, contour) => ({ ar: 1.487, car, door, contour, by: "ai" });
export const BUILT_IN_ANCHORS = {
  rangerover: A({ x: 36.6, y: 31.6, w: 58.9, h: 44.4 }, { x: 71.2, y: 45.4, w: 11.5, h: 17.8 }, [[39, 46.5], [47.5, 44.2], [52.8, 43.8], [61.2, 33.6], [76, 31.6], [90.5, 33.2], [94.5, 47.5]]),
  gwagon: A({ x: 33.7, y: 24.7, w: 61.8, h: 56.7 }, { x: 74.7, y: 43.5, w: 9.6, h: 18.7 }, [[37.5, 44.7], [44.7, 43.1], [51.7, 40.3], [55.5, 29], [77, 25], [90.2, 27.9], [94.6, 47.6]]),
  escalade: A({ x: 36.2, y: 28.7, w: 60.9, h: 50.5 }, { x: 72.9, y: 44.7, w: 12.7, h: 21.6 }, [[38.8, 43.2], [48, 42.1], [57.4, 42.2], [59.9, 31.1], [68, 31.5], [78.5, 30], [89.3, 32], [95.1, 37.6], [96.6, 53.4]]),
  cayenne: A({ x: 39.5, y: 34, w: 56.3, h: 41 }, { x: 74.5, y: 45.5, w: 9.5, h: 18 }, [[41.5, 48], [53, 44.5], [60.5, 42.2], [67.5, 35.2], [78, 34.2], [88.5, 36], [92, 41.5], [95.5, 50]]),
  sclass: A({ x: 33.7, y: 31.4, w: 62.6, h: 43.3 }, { x: 73.5, y: 42.6, w: 13.1, h: 22.4 }, [[36.8, 48], [47.7, 44.7], [52.9, 43], [58.4, 35.4], [66.5, 31.9], [76.5, 31.5], [84.3, 33], [91.1, 40.3], [96.1, 53.4]]),
  bmw7: A({ x: 40.3, y: 36.3, w: 55.5, h: 38.2 }, { x: 76.5, y: 46.5, w: 9.5, h: 18.5 }, [[42.2, 51.5], [49, 47.5], [56, 46], [60.5, 40.5], [66.5, 37], [75, 36.5], [81.5, 37], [89, 41.5], [95, 48]]),
};
export const DEFAULT_ANCHOR = A({ x: 35, y: 30, w: 60, h: 45 }, { x: 68, y: 46, w: 14, h: 18 }, [[38, 50], [50, 44], [62, 34], [76, 31], [90, 34], [95, 48]]);

export const readAnchor = (site, src, carId) => {
  try {
    const raw = site(anchorKey(src));
    if (raw && raw.startsWith("{")) return JSON.parse(raw);
  } catch { /* fall through */ }
  return BUILT_IN_ANCHORS[carId] || DEFAULT_ANCHOR;
};

// Map image-% points to screen px for an object-fit: cover image (object-position pos 0..1)
export const coverMap = (anchor, vw, vh, pos = { x: 0.5, y: 0.5 }, minW = 0) => {
  const ar = anchor?.ar || 1.5;
  const s = Math.max(vw / ar, vh, minW / ar);
  const w = s * ar;
  const h = s;
  const ox = (vw - w) * pos.x;
  const oy = (vh - h) * pos.y;
  const pt = (x, y) => [ox + (x / 100) * w, oy + (y / 100) * h];
  const box = (b) => { const [x, y] = pt(b.x, b.y); return { x, y, w: (b.w / 100) * w, h: (b.h / 100) * h }; };
  return { pt, box, scale: w / 100, rect: { left: ox, top: oy, width: w, height: h } };
};

// Owner-chosen car position (0 = left, 50 = center, 100 = right): zooms just enough to slide the photo sideways
export const placeMap = (anchor, vw, vh, carPos = 50) => {
  const want = 0.2 + (Math.max(0, Math.min(100, carPos)) / 100) * 0.6; // screen spot (0..1) for the car's center
  const cx = Math.max(0.05, Math.min(0.95, (anchor?.car ? anchor.car.x + anchor.car.w / 2 : 50) / 100));
  const minW = Math.min(vw * 1.5, Math.max(vw, (want * vw) / cx, (vw * (1 - want)) / (1 - cx)));
  const base = coverMap(anchor, vw, vh, { x: 0.5, y: 0.5 }, minW);
  const W = base.rect.width;
  if (W <= vw + 1) return base;
  const ox = Math.max(vw - W, Math.min(0, want * vw - cx * W));
  return coverMap(anchor, vw, vh, { x: ox / (vw - W), y: 0.5 }, minW);
};

// Phones: zoom so the whole car spans the screen width in the middle band, lettering above, buttons below
export const containMap = (anchor, vw, vh) => {
  const ar = anchor?.ar || 1.5;
  const c = anchor?.car || { x: 0, y: 0, w: 100, h: 100 };
  let ww = (vw * 0.98) / (c.w / 100);
  ww = Math.min(ww, (vh * 0.42 * ar) / (c.h / 100)); // car never taller than 42% of the screen
  const h = ww / ar;
  const carH = (c.h / 100) * h;
  const bandTop = Math.max(200, (vh - carH) / 2 - 10);
  const left = vw / 2 - ((c.x + c.w / 2) / 100) * ww;
  const top = bandTop - (c.y / 100) * h;
  const pt = (x, y) => [left + (x / 100) * ww, top + (y / 100) * h];
  const box = (b) => { const [x, y] = pt(b.x, b.y); return { x, y, w: (b.w / 100) * ww, h: (b.h / 100) * h }; };
  const fade = `linear-gradient(to bottom, transparent ${c.y - 30}%, #000 ${c.y - 4}%, #000 ${c.y + c.h + 4}%, transparent ${c.y + c.h + 28}%)`;
  return { pt, box, scale: ww / 100, rect: { left, top, width: ww, height: h }, band: { top: bandTop, bottom: bandTop + carH }, mask: fade, contain: true };
};

export const CAR_POSITIONS = [{ id: 22, label: "Left" }, { id: 50, label: "Center" }, { id: 78, label: "Right" }];

export const useBox = (ref) => {
  const [size, setSize] = useState({ w: 0, h: 0 });
  useEffect(() => {
    if (!ref.current) return undefined;
    const ro = new ResizeObserver(([e]) => setSize({ w: e.contentRect.width, h: e.contentRect.height }));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, [ref]);
  return size;
};

export const HERO_LAYOUTS = [
  { id: "contour", label: "Follow the car's outline", desc: "Headline curves along the roof and hood" },
  { id: "door", label: "On the door", desc: "Headline and buttons sit on the front door, like a decal" },
  { id: "classic", label: "Classic left", desc: "Headline, text and buttons on the left" },
  { id: "center", label: "Big & centered", desc: "Huge centered headline over the car" },
  { id: "banner", label: "Bottom banner", desc: "Glass bar along the bottom with the buttons" },
  { id: "split", label: "Split", desc: "Headline top-left, buttons bottom-right" },
];
export const HERO_ANIMS = [
  { id: "zoom", label: "Zoom into the door", desc: "One scroll zooms the camera onto the door so the lettering and Book Now come close" },
  { id: "fade", label: "Fade & rise", desc: "Lettering floats up and fades as you scroll" },
  { id: "drift", label: "Slow drift", desc: "Car drifts slower than the page (parallax)" },
  { id: "none", label: "No animation", desc: "Everything stays still" },
];

export const VIDEO_PRESETS = [
  { id: "drive", label: "Drive in & park", motion: "drive", prompt: "The freshly detailed car drives slowly into the shot from the left with its headlights on, glides across the glossy floor and parks, then stays perfectly still" },
  { id: "wet", label: "Still & wet", prompt: "Heavy water droplets clearly running down the doors, hood and glass, big drips falling from the mirrors and bumper, wet glossy paint, the car itself stays still" },
  { id: "soap", label: "Soap foam", prompt: "Thick white snow foam visibly covering the whole car and slowly sliding down the paint in big soft sheets, foam dripping onto the floor, bubbles glistening, the car itself stays still" },
  { id: "ceramic", label: "Ceramic beading", prompt: "Water is sprayed onto the hood and door and instantly forms tight round beads that visibly roll off the glossy ceramic-coated paint", overlay: "ceramic" },
  { id: "rinse", label: "Sheeting rinse", prompt: "A strong stream of rinse water pours over the roof and visibly sheets off the hood and doors in waves, leaving mirror-glossy paint" },
  { id: "lights", label: "Showroom lights", prompt: "Bright studio light bars glide slowly across the paint creating strong moving reflections along the whole car in a dark showroom" },
  { id: "rain", label: "Night rain", prompt: "Night, heavy rain visibly falling around the car, droplets splashing and beading on the glossy paint, puddles rippling and reflecting the car" },
  { id: "steam", label: "Steam clean", prompt: "Thick white steam clearly billowing and drifting around the wheels and lower doors like a fresh steam detail" },
  { id: "sunset", label: "Desert sunset", prompt: "Warm Arizona sunset light visibly sweeping across the paint, long moving shadows, gentle heat shimmer" },
  { id: "polish", label: "Polish shine", prompt: "An orbital polisher clearly glides across the door, leaving a deep mirror shine behind it" },
  { id: "headlights", label: "Headlights on", prompt: "The headlights and DRLs switch on brightly, light mist drifting through the beams" },
];

// Which map the hero uses: phones (portrait) show the whole car; desktops honour the chosen car position
export const mapFor = (anchor, vw, vh, carPos) => {
  if (!vw) return null;
  if (vh > vw) return containMap(anchor, vw, vh);
  return carPos !== "" && carPos != null ? placeMap(anchor, vw, vh, Number(carPos)) : coverMap(anchor, vw, vh);
};

// Headline letter looks: sx/sy stretch, ls letter-spacing (em), wt weight
export const TYPE_LOOKS = [
  { id: "classic", label: "Classic", sx: 1, sy: 1, ls: 0.02, wt: 500 },
  { id: "wide", label: "Wide", sx: 1.35, sy: 0.9, ls: 0.08, wt: 500 },
  { id: "tall", label: "Tall", sx: 0.82, sy: 1.35, ls: 0, wt: 600 },
  { id: "tight", label: "Tight", sx: 1, sy: 1, ls: -0.04, wt: 700 },
  { id: "bold", label: "Bold", sx: 1.1, sy: 1.08, ls: 0.01, wt: 700 },
  { id: "airy", label: "Airy", sx: 1.15, sy: 1, ls: 0.2, wt: 300 },
];
export const parseType = (raw) => { try { return { ...TYPE_LOOKS[0], ...JSON.parse(raw) }; } catch { return TYPE_LOOKS[0]; } };
export const parseList = (raw) => { try { const v = JSON.parse(raw); return Array.isArray(v) ? v : []; } catch { return []; } };
